// ==================== COUNTDOWN TIMER COMPONENT ====================
/**
 * SAFE PATCH: Contador regressivo para agendamentos.
 * Mostra quanto tempo falta para o serviço agendado.
 * 
 * Formato:
 * - > 24h: "Xd Yh"
 * - > 1h: "Xh Ym"
 * - < 1h: "Xm"
 * - < 1min: "Agora"
 * - Passado: "Atrasado"
 */
import React, { useState, useEffect } from 'react';
import { Clock, Hourglass, AlertCircle } from 'lucide-react';

/**
 * Formata o tempo restante de forma amigável.
 * @param {number} diffMs - Diferença em milissegundos
 * @returns {object} - { text, isNow, isPast, urgency }
 */
const formatTimeRemaining = (diffMs) => {
  // Se passou do horário
  if (diffMs < 0) {
    const pastMs = Math.abs(diffMs);
    const pastMinutes = Math.floor(pastMs / 60000);
    
    if (pastMinutes < 30) {
      return { 
        text: `Há ${pastMinutes}m`, 
        isNow: false, 
        isPast: true, 
        urgency: 'warning' 
      };
    }
    return { 
      text: 'Atrasado', 
      isNow: false, 
      isPast: true, 
      urgency: 'danger' 
    };
  }
  
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  // Menos de 1 minuto
  if (minutes < 1) {
    return { 
      text: 'Agora', 
      isNow: true, 
      isPast: false, 
      urgency: 'now' 
    };
  }
  
  // Menos de 1 hora
  if (hours < 1) {
    return { 
      text: `${minutes}m`, 
      isNow: false, 
      isPast: false, 
      urgency: minutes <= 15 ? 'urgent' : 'soon' 
    };
  }
  
  // Menos de 24 horas
  if (days < 1) {
    const remainingMinutes = minutes % 60;
    return { 
      text: `${hours}h ${remainingMinutes}m`, 
      isNow: false, 
      isPast: false, 
      urgency: hours <= 2 ? 'soon' : 'normal' 
    };
  }
  
  // Mais de 24 horas
  const remainingHours = hours % 24;
  return { 
    text: `${days}d ${remainingHours}h`, 
    isNow: false, 
    isPast: false, 
    urgency: 'normal' 
  };
};

/**
 * Componente de contador regressivo.
 * 
 * @param {string} date - Data no formato YYYY-MM-DD
 * @param {string} time - Horário no formato HH:MM
 * @param {string} className - Classes CSS adicionais
 * @param {boolean} showIcon - Mostrar ícone (default: true)
 * @param {boolean} showLabel - Mostrar "Faltam" antes do tempo (default: true)
 * @param {string} size - Tamanho: 'sm', 'md', 'lg' (default: 'md')
 */
export default function AppointmentCountdown({ 
  date, 
  time, 
  className = '', 
  showIcon = true,
  showLabel = true,
  size = 'md'
}) {
  const [timeInfo, setTimeInfo] = useState(null);
  
  useEffect(() => {
    if (!date || !time) return;
    
    const calculateTime = () => {
      try {
        const appointmentDateTime = new Date(`${date}T${time}:00`);
        const now = new Date();
        const diffMs = appointmentDateTime - now;
        
        setTimeInfo(formatTimeRemaining(diffMs));
      } catch (error) {
        console.error('[Countdown] Erro ao calcular tempo:', error);
        setTimeInfo(null);
      }
    };
    
    // Calcular imediatamente
    calculateTime();
    
    // Atualizar a cada 60 segundos
    const interval = setInterval(calculateTime, 60000);
    
    return () => clearInterval(interval);
  }, [date, time]);
  
  if (!timeInfo) return null;
  
  // Configuração de estilos por urgência
  const urgencyStyles = {
    now: {
      container: 'bg-green-100 text-green-800 border-green-300',
      icon: 'text-green-600',
      pulse: true
    },
    urgent: {
      container: 'bg-orange-100 text-orange-800 border-orange-300',
      icon: 'text-orange-600',
      pulse: true
    },
    soon: {
      container: 'bg-amber-50 text-amber-800 border-amber-300',
      icon: 'text-amber-600',
      pulse: false
    },
    normal: {
      container: 'bg-blue-50 text-blue-700 border-blue-200',
      icon: 'text-blue-500',
      pulse: false
    },
    warning: {
      container: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      icon: 'text-yellow-600',
      pulse: false
    },
    danger: {
      container: 'bg-red-100 text-red-800 border-red-300',
      icon: 'text-red-600',
      pulse: false
    }
  };
  
  const style = urgencyStyles[timeInfo.urgency] || urgencyStyles.normal;
  
  // Tamanhos
  const sizes = {
    sm: {
      container: 'px-2 py-1 text-xs',
      icon: 'w-3 h-3'
    },
    md: {
      container: 'px-3 py-1.5 text-sm',
      icon: 'w-4 h-4'
    },
    lg: {
      container: 'px-4 py-2 text-base',
      icon: 'w-5 h-5'
    }
  };
  
  const sizeStyle = sizes[size] || sizes.md;
  
  // Escolher ícone
  const Icon = timeInfo.isNow ? Clock : timeInfo.isPast ? AlertCircle : Hourglass;
  
  return (
    <div 
      className={`
        inline-flex items-center gap-1.5 rounded-full font-medium border
        ${style.container} 
        ${sizeStyle.container}
        ${style.pulse ? 'animate-pulse' : ''}
        ${className}
      `}
      data-testid="appointment-countdown"
    >
      {showIcon && (
        <Icon className={`${sizeStyle.icon} ${style.icon}`} />
      )}
      <span>
        {showLabel && !timeInfo.isNow && !timeInfo.isPast && 'Faltam '}
        {timeInfo.isPast && !timeInfo.text.startsWith('Há') && ''}
        <strong>{timeInfo.text}</strong>
      </span>
    </div>
  );
}

/**
 * Hook para usar o countdown em outros componentes.
 * 
 * @param {string} date - Data no formato YYYY-MM-DD
 * @param {string} time - Horário no formato HH:MM
 * @returns {object} - { text, isNow, isPast, urgency, minutesRemaining }
 */
export function useCountdown(date, time) {
  const [timeInfo, setTimeInfo] = useState(null);
  
  useEffect(() => {
    if (!date || !time) return;
    
    const calculateTime = () => {
      try {
        const appointmentDateTime = new Date(`${date}T${time}:00`);
        const now = new Date();
        const diffMs = appointmentDateTime - now;
        const minutesRemaining = Math.floor(diffMs / 60000);
        
        const formatted = formatTimeRemaining(diffMs);
        setTimeInfo({
          ...formatted,
          minutesRemaining
        });
      } catch (error) {
        setTimeInfo(null);
      }
    };
    
    calculateTime();
    const interval = setInterval(calculateTime, 60000);
    
    return () => clearInterval(interval);
  }, [date, time]);
  
  return timeInfo;
}
